import React, { useState, useEffect } from 'react';
import { X, Link, CheckCircle, Spinner, Copy, ArrowRight, Lightning, ShoppingCart, Envelope, Users, FileText } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api from '@/utils/api';
import { getUser } from '@/utils/auth';
import { useFormDefaults } from '@/store/usePayGBotContext';
import OutputActions from './OutputActions';

const LinkInBioModal = ({ isOpen, onClose, onSuccess, onCreateContent }) => {
  const user = getUser();
  const formDefaults = useFormDefaults();
  
  const [step, setStep] = useState('input'); // 'input', 'generating', 'result'
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  
  const [formData, setFormData] = useState({
    niche: '',
    offer_name: '',
    offer_description: '',
    platform_preference: 'stan_store',
    link_count: 4,
    include_freebie: true
  });

  // Auto-fill from global business context
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        niche: formDefaults.niche || user?.niche || prev.niche,
        offer_name: prev.offer_name,
        offer_description: prev.offer_description
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, formDefaults.niche, user?.niche]);

  const platformOptions = [
    { id: 'stan_store', label: 'Stan Store', desc: 'Best for selling' },
    { id: 'linktree', label: 'Linktree', desc: 'Simple & free' },
    { id: 'beacons', label: 'Beacons', desc: 'All-in-one' },
    { id: 'bio_sites', label: 'Bio.site', desc: 'Beautiful designs' }
  ];

  const handleSubmit = async () => {
    if (!formData.niche) {
      toast.error('Please enter your niche');
      return;
    }

    setStep('generating');
    setLoading(true);

    try {
      const response = await api.post('/monetization/link-in-bio', {
        niche: formData.niche,
        offer_name: formData.offer_name,
        offer_description: formData.offer_description,
        platform_preference: formData.platform_preference,
        link_count: formData.link_count,
        include_freebie: formData.include_freebie
      });
      
      setResult(response.data);
      setStep('result');
      
      if (onSuccess) onSuccess();
      toast.success('Your link in bio setup is ready!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate link in bio setup');
      setStep('input');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleClose = () => {
    setStep('input');
    setResult(null);
    onClose();
  };

  const copyAllLinks = () => {
    if (!result?.links) return;
    
    const allLinks = result.links.map((link, i) => 
      `${i + 1}. ${link.text}\n   Type: ${link.url_type}\n   CTA: ${link.cta}`
    ).join('\n\n');
    
    const fullContent = `LINK IN BIO SETUP\n\nBio Text:\n${result.bio_text}\n\nLinks:\n${allLinks}`;
    
    navigator.clipboard.writeText(fullContent);
    toast.success('All content copied!');
  };

  const getLinkIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'product':
      case 'offer':
        return <ShoppingCart size={16} className="text-emerald-400" />;
      case 'freebie':
      case 'lead_magnet':
        return <FileText size={16} className="text-blue-400" />;
      case 'email':
      case 'newsletter':
        return <Envelope size={16} className="text-purple-400" />;
      case 'community':
      case 'social':
        return <Users size={16} className="text-pink-400" />;
      default:
        return <Link size={16} className="text-slate-400" />;
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-2xl paygbot-modal-shell bg-slate-900 rounded-2xl shadow-2xl border border-white/10"
        >
          {/* Header */}
          <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center">
                <Link size={20} className="text-white" weight="fill" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Create Link in Bio</h2>
                <p className="text-sm text-slate-400">Your perfect link structure for conversions</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X size={18} className="text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Input Step */}
            {step === 'input' && (
              <div className="space-y-6">
                {/* Niche */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    What's your niche?
                  </label>
                  <input
                    type="text"
                    value={formData.niche}
                    onChange={(e) => setFormData({...formData, niche: e.target.value})}
                    placeholder="e.g., Fitness coaching, Digital products"
                    data-testid="link-bio-niche"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>

                {/* Offer Name (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Main offer name (optional)
                  </label>
                  <input
                    type="text"
                    value={formData.offer_name}
                    onChange={(e) => setFormData({...formData, offer_name: e.target.value})}
                    placeholder="e.g., 30-Day Fitness Transformation"
                    data-testid="link-bio-offer-name"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>

                {/* Platform Preference */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Preferred platform
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {platformOptions.map((platform) => (
                      <button
                        key={platform.id}
                        onClick={() => setFormData({...formData, platform_preference: platform.id})}
                        className={`p-3 rounded-lg text-left transition-all ${
                          formData.platform_preference === platform.id
                            ? 'bg-emerald-500/20 border-emerald-500/50 border'
                            : 'bg-white/5 border-white/10 border hover:bg-white/10'
                        }`}
                      >
                        <span className={`text-sm font-medium block ${formData.platform_preference === platform.id ? 'text-emerald-400' : 'text-white'}`}>
                          {platform.label}
                        </span>
                        <span className="text-xs text-slate-500">{platform.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Link Count */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    How many links?
                  </label>
                  <div className="flex space-x-2">
                    {[3, 4, 5, 6].map((count) => (
                      <button
                        key={count}
                        onClick={() => setFormData({...formData, link_count: count})}
                        className={`flex-1 p-3 rounded-lg text-center transition-all ${
                          formData.link_count === count
                            ? 'bg-emerald-500/20 border-emerald-500/50 border'
                            : 'bg-white/5 border-white/10 border hover:bg-white/10'
                        }`}
                      >
                        <span className={`text-sm font-medium ${formData.link_count === count ? 'text-emerald-400' : 'text-white'}`}>
                          {count}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Include Freebie */}
                <div>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.include_freebie}
                      onChange={(e) => setFormData({...formData, include_freebie: e.target.checked})}
                      className="w-5 h-5 rounded border-white/20 bg-white/5 text-emerald-500 focus:ring-emerald-500"
                    />
                    <span className="text-slate-300 text-sm">Include a freebie/lead magnet link</span>
                  </label>
                </div>

                <button
                  onClick={handleSubmit}
                  data-testid="link-bio-submit"
                  className="w-full py-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold rounded-xl flex items-center justify-center space-x-2 transition-all"
                >
                  <Link size={20} weight="fill" />
                  <span>Create My Link Setup</span>
                </button>
              </div>
            )}

            {/* Generating Step */}
            {step === 'generating' && (
              <div className="py-12 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Spinner size={32} className="text-white animate-spin" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Creating Your Link Setup</h3>
                <p className="text-slate-400">Building the perfect bio and link structure for {formData.platform_preference}...</p>
              </div>
            )}

            {/* Result Step */}
            {step === 'result' && result && (
              <div className="space-y-6">
                {/* Platform Suggestion */}
                <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30">
                  <div className="flex items-center space-x-2 mb-2">
                    <Lightning size={20} className="text-emerald-400" weight="fill" />
                    <span className="text-emerald-400 font-semibold">Recommended Platform</span>
                  </div>
                  <p className="text-white">{result.platform_suggestion || formData.platform_preference}</p>
                </div>

                {/* Bio Text */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-slate-300">Your Bio Text</label>
                    <button
                      onClick={() => handleCopy(result.bio_text, 'bio-text')}
                      className="text-slate-500 hover:text-white text-xs flex items-center space-x-1"
                    >
                      {copiedField === 'bio-text' ? <CheckCircle size={12} /> : <Copy size={12} />}
                      <span>Copy</span>
                    </button>
                  </div>
                  <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
                    <p className="text-white text-sm whitespace-pre-wrap">{result.bio_text}</p>
                  </div>
                </div>

                {/* Copy All Button */}
                <button
                  onClick={copyAllLinks}
                  className="w-full py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors flex items-center justify-center space-x-2"
                >
                  <Copy size={18} />
                  <span>Copy All Content</span>
                </button>

                {/* Links */}
                <div className="space-y-3">
                  <h4 className="text-white font-medium">Your Link Structure</h4>
                  {result.links?.map((link, i) => (
                    <div 
                      key={i} 
                      className="bg-white/5 border border-white/10 rounded-xl p-4"
                      data-testid={`link-bio-link-${i}`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          {getLinkIcon(link.url_type)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-white font-medium text-sm">{link.text}</span>
                            <button
                              onClick={() => handleCopy(`${link.text}\nCTA: ${link.cta}`, `link-${i}`)}
                              className="text-slate-500 hover:text-white"
                            >
                              {copiedField === `link-${i}` ? <CheckCircle size={14} /> : <Copy size={14} />}
                            </button>
                          </div>
                          <div className="flex items-center space-x-2 mb-2">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                              link.url_type === 'product' ? 'bg-emerald-500/20 text-emerald-400' :
                              link.url_type === 'freebie' ? 'bg-blue-500/20 text-blue-400' :
                              link.url_type === 'newsletter' ? 'bg-purple-500/20 text-purple-400' :
                              'bg-slate-500/20 text-slate-400'
                            }`}>
                              {link.url_type}
                            </span>
                          </div>
                          <p className="text-emerald-400 text-xs bg-emerald-500/10 p-2 rounded">
                            CTA: "{link.cta}"
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Tips */}
                {result.tips && result.tips.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-white font-medium">Pro Tips</h4>
                    <ul className="space-y-2">
                      {result.tips.map((tip, i) => (
                        <li key={i} className="flex items-start space-x-2 text-slate-300 text-sm bg-white/5 p-3 rounded-lg">
                          <CheckCircle size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Output Actions - Copy, Download PDF, Save */}
                <div className="pt-4 border-t border-white/10">
                  <OutputActions
                    content={`LINK IN BIO SETUP\n\nBio Text:\n${result.bio_text}\n\nLinks:\n${result.links?.map((l, i) => `${i+1}. ${l.text} (${l.url_type})\n   CTA: ${l.cta}`).join('\n\n')}`}
                    pdfType="link_in_bio"
                    pdfData={{
                      platform_suggestion: result.platform_suggestion,
                      bio_text: result.bio_text,
                      links: result.links
                    }}
                    saveType="link_in_bio"
                    saveTitle={`Link in Bio - ${formData.niche}`}
                    saveContent={{
                      niche: formData.niche,
                      platform: formData.platform_preference,
                      bio_text: result.bio_text,
                      links: result.links,
                      tips: result.tips
                    }}
                  />
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-3 pt-4">
                  {/* Primary CTA - Create Content */}
                  <button
                    onClick={() => {
                      onCreateContent?.();
                      handleClose();
                    }}
                    data-testid="create-content-btn"
                    className="w-full py-3.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center space-x-2"
                  >
                    <Lightning size={20} weight="fill" />
                    <span>Create Content to Promote</span>
                    <ArrowRight size={18} />
                  </button>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={() => { setStep('input'); setResult(null); }}
                      className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors"
                    >
                      Create New Setup
                    </button>
                    <button
                      onClick={handleClose}
                      className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default LinkInBioModal;
